// NATIVE
import React, { useEffect, useState } from 'react';

// INSTALLED
import { useSelector } from 'react-redux';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from '@mui/material';

// CUSTOM
import { useFormikContext } from 'formik';
import { paddingCardsTables } from '../../commons';
import { IUserPermissions } from '../../commons/interfaces';
import { eachWordUpper } from '../../commons/utils';
import { SinDatos } from '../generics/SinDatos';

const RenderChildren = ({
  element,
  parentSlugs,
  isParentEnabled,
  selectedModules,
  selectedActions,
  handleToggleModule,
  handleToggleAction,
}: any) => {
  const key = element.slug || element.permission_slug;
  const isModuleEnabled = !!selectedModules[key];
  const isCheckboxDisabled = !isParentEnabled;
  const isActionDisabled = isCheckboxDisabled || !isModuleEnabled;

  const myParentSlugs = [...parentSlugs, key];

  // Lista de acciones disponibles en el sistema
  const defaultActions = ['view', 'create', 'edit', 'delete', 'restore'];

  const isTopLevel = parentSlugs.length === 0;

  return (
    <Accordion
      sx={
        isTopLevel
          ? {
              mt: 3,
              borderTop: 4,
              borderColor: '#192a56',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 3,
            }
          : { mt: 1, border: '1px solid #e0e0e0', boxShadow: 'none' }
      }
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={isTopLevel ? { color: '#192a56' } : {}} />}
        sx={isTopLevel ? { backgroundColor: '#f0f4f8' } : { backgroundColor: '#fafafa' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {isTopLevel ? (
            <>
              <Typography
                component="div"
                variant="h6"
                sx={{
                  color: '#192a56',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {eachWordUpper(element.text)} <VerifiedIcon fontSize="small" />
              </Typography>
              <Typography variant="body2" component="div" sx={{ color: 'text.secondary' }}>
                {element.subText ? eachWordUpper(element.subText) : 'Sub módulos y permisos'}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight="bold">
                {eachWordUpper(element.text)}
              </Typography>
              {element.subText && (
                <Typography variant="body2" color="text.secondary">
                  {eachWordUpper(element.subText)}
                </Typography>
              )}
            </>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: '#fff', padding: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ pb: 1, borderBottom: '1px solid #e0e0e0' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isModuleEnabled}
                    disabled={isCheckboxDisabled}
                    onChange={(e) => handleToggleModule(key, e.target.checked, parentSlugs)}
                    name={`mod_${key}`}
                  />
                }
                label="Módulo habilitado"
              />
            </FormGroup>
          </Box>

          {(!element.childrens || element.childrens.length === 0) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 2, pb: 2 }}>
              {defaultActions.map((action) => {
                const actionEnabled = (selectedActions[key] || []).includes(action);
                return (
                  <FormGroup key={action}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={actionEnabled}
                          disabled={isActionDisabled}
                          onChange={(e) => handleToggleAction(key, action, e.target.checked)}
                          name={`${action}_${key}`}
                        />
                      }
                      label={
                        action === 'view'
                          ? 'Ver'
                          : action === 'create'
                            ? 'Crear'
                            : action === 'edit'
                              ? 'Editar'
                              : action === 'delete'
                                ? 'Eliminar'
                                : 'Restaurar'
                      }
                    />
                  </FormGroup>
                );
              })}
            </Box>
          )}

          {element.childrens && element.childrens.length > 0 && (
            <Box sx={{ mt: 1, ml: 2, borderLeft: '2px solid #f0f0f0', pl: 2 }}>
              {element.childrens.map((child: any, idx: number) => (
                <RenderChildren
                  key={idx}
                  element={child}
                  parentSlugs={myParentSlugs}
                  isParentEnabled={isModuleEnabled}
                  selectedModules={selectedModules}
                  selectedActions={selectedActions}
                  handleToggleModule={handleToggleModule}
                  handleToggleAction={handleToggleAction}
                />
              ))}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const EditUserSystemPermissions = (userWithPermissions: IUserPermissions) => {
  const formikProps: any = useFormikContext();

  // REDUX
  const { userPemissionsAssigned }: any = useSelector((state: any) => state.usersPermissions);

  // N-LEVEL DYNAMIC STATE (O(1) lookups)
  const [selectedModules, setSelectedModules] = useState<Record<string, boolean>>({});
  const [selectedActions, setSelectedActions] = useState<Record<string, string[]>>({});

  // Initialize selected modules and actions from userPemissionsAssigned dynamically
  useEffect(() => {
    if (!userPemissionsAssigned || userPemissionsAssigned.length === 0) return;

    const initModules: Record<string, boolean> = {};
    const initActions: Record<string, string[]> = {};

    const extract = (nodes: any[]) => {
      if (!nodes) return;
      for (const node of nodes) {
        if (node.isChecked) {
          const key = node.slug || node.permission_slug;
          initModules[key] = true;
          initActions[key] = node.actions || [];
        }
        if (node.childrens && node.childrens.length > 0) {
          extract(node.childrens);
        }
      }
    };

    extract(userPemissionsAssigned);
    setSelectedModules(initModules);
    setSelectedActions(initActions);
  }, [userPemissionsAssigned]);

  // Rebuild the payload dynamically whenever selections change and sync with Formik
  useEffect(() => {
    if (!userPemissionsAssigned || userPemissionsAssigned.length === 0) return;

    const buildPayload = (nodes: any[]): any[] => {
      if (!nodes) return [];

      const result: any[] = [];
      for (const node of nodes) {
        const key = node.slug || node.permission_slug;
        if (selectedModules[key]) {
          const newNode = { ...node };
          newNode.isChecked = true;
          newNode.actions = selectedActions[key] || [];
          newNode.childrens = buildPayload(node.childrens);
          result.push(newNode);
        }
      }
      return result;
    };

    const modules = buildPayload(userPemissionsAssigned);

    // Solo actualizar si hay una diferencia para evitar bucles infinitos en Formik
    formikProps.setFieldValue('permissions', modules);
    formikProps.setFieldValue('userPermissionId', userWithPermissions.id);
  }, [selectedModules, selectedActions, userPemissionsAssigned, userWithPermissions.id]);

  const handleToggleModule = (slug: string, isChecked: boolean, parentSlugs: string[]) => {
    setSelectedModules((prev) => {
      const next = { ...prev };
      next[slug] = isChecked;
      if (isChecked) {
        // Al habilitar un módulo, auto-habilitar recursivamente todos sus padres
        parentSlugs.forEach((pSlug) => {
          next[pSlug] = true;
        });
      }
      return next;
    });
  };

  const handleToggleAction = (slug: string, action: string, isChecked: boolean) => {
    setSelectedActions((prev) => {
      const next = { ...prev };
      const currentActions = next[slug] || [];
      if (isChecked) {
        if (!currentActions.includes(action)) {
          next[slug] = [...currentActions, action];
        }
      } else {
        next[slug] = currentActions.filter((a) => a !== action);
      }
      return next;
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
        <Card sx={{ marginTop: paddingCardsTables }}>
          <Box sx={{ width: '100%' }}>
            {userPemissionsAssigned && userPemissionsAssigned.length > 0 ? (
              userPemissionsAssigned.map((element: any, idx: number) => (
                <RenderChildren
                  key={idx}
                  element={element}
                  parentSlugs={[]}
                  isParentEnabled={true} // Nivel 0 siempre habilitado para hacer toggle
                  selectedModules={selectedModules}
                  selectedActions={selectedActions}
                  handleToggleModule={handleToggleModule}
                  handleToggleAction={handleToggleAction}
                />
              ))
            ) : (
              <SinDatos />
            )}
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};
